import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Mail, Eye, CheckCheck, AlertTriangle, ChevronLeft, Loader2, Info } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { API_BASE as API } from '../config';
import { DeliveryPill } from '../components/email/DeliveryPill';
import { useAuth } from '../contexts/AuthContext';

/**
 * Full-page Email Engagement report — reachable from the user-menu dropdown.
 *
 * Provides three things the inline menu-line cannot:
 *   • `Mine` ↔ `Org` toggle (per-user vs tenant-wide totals)
 *   • Window switcher (7 / 30 / 90 days)
 *   • Recent outbound emails table with the DeliveryPill on each row
 *
 * The DeliveryPill itself is the same one rendered inside NotesModal /
 * EmailThreadModal so the visual vocabulary is consistent across the app.
 */
const WINDOWS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const StatTile = ({ Icon, label, value, sub, color, testid }) => (
  <Card className="border-none shadow-sm bg-white" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[11px] uppercase tracking-wide font-semibold text-gray-500">{label}</span>
      </div>
      <p className="text-3xl font-bold text-gray-900 leading-none">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1.5">{sub}</p>}
    </CardContent>
  </Card>
);

const EmailEngagement = () => {
  const { user } = useAuth();
  const currentUserId = user?.user_id;
  const [scope, setScope] = useState('mine');     // 'mine' | 'org'
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(true);
  // Webhook diagnostics — only used to surface a "subscribe to event types
  // in Resend" banner when the user has sent emails but no engagement
  // events have actually arrived. Fetched once on mount.
  const [diagnostics, setDiagnostics] = useState(null);

  // Stats fetch — reruns when scope or window changes.
  useEffect(() => {
    let alive = true;
    setStatsLoading(true);
    axios.get(`${API}/email/engagement-stats?days=${days}&scope=${scope}`)
      .then((r) => { if (alive) setStats(r.data); })
      .catch(() => { if (alive) setStats(null); })
      .finally(() => { if (alive) setStatsLoading(false); });
    return () => { alive = false; };
  }, [scope, days]);

  // One-shot diagnostics fetch — banner only renders when something looks
  // off, so a single check at mount time is enough.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/email/webhook-diagnostics`)
      .then((r) => { if (alive) setDiagnostics(r.data); })
      .catch(() => { if (alive) setDiagnostics(null); });
    return () => { alive = false; };
  }, []);

  // Recent emails feed — uses the tenant-scoped /api/inbox endpoint with a
  // direction=sent filter. We deliberately do NOT use /claim-emails here
  // because that endpoint requires a `report_no` and would 422 without one;
  // the inbox endpoint has no such requirement and already handles tenant
  // scoping + projection of all the delivery fields we need. Fetch a wide
  // window (200) so the client-side scope+days filter has enough headroom
  // even for busy tenants.
  useEffect(() => {
    let alive = true;
    setEmailsLoading(true);
    axios.get(`${API}/inbox`, { params: { direction: 'sent', limit: 200 } })
      .then((r) => {
        if (!alive) return;
        const items = Array.isArray(r.data?.items) ? r.data.items : [];
        setEmails(items);
      })
      .catch(() => { if (alive) setEmails([]); })
      .finally(() => { if (alive) setEmailsLoading(false); });
    return () => { alive = false; };
  }, []);

  // Apply scope + window client-side on the recent list. The summary stats
  // come from the server (same window/scope), so the totals always match.
  const filteredEmails = useMemo(() => {
    if (!emails.length) return [];
    const cutoff = Date.now() - days * 86400 * 1000;
    return emails.filter((m) => {
      if (m.direction !== 'sent') return false;
      const t = m.sent_at || m.created_at;
      if (!t || new Date(t).getTime() < cutoff) return false;
      // The list endpoint now returns created_by_user_id, so the recent
      // feed stays consistent with the Mine-scoped headline counters.
      if (scope === 'mine') return m.created_by_user_id === currentUserId;
      return true;
    }).slice(0, 50);
  }, [emails, days, scope, currentUserId]);

  const totalSent = stats?.total_sent ?? 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-2" data-testid="engagement-back">
              <ChevronLeft className="w-3.5 h-3.5" /> Back to dashboard
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Email Engagement</h1>
            <p className="text-sm text-gray-500 mt-1">
              Delivery and open rates for outbound claim emails.
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden bg-white" role="tablist" data-testid="engagement-scope">
            {['mine', 'org'].map((s) => (
              <button
                key={s}
                onClick={() => setScope(s)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  scope === s ? 'bg-teal-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`engagement-scope-${s}`}
              >
                {s === 'mine' ? 'Mine' : 'Org-wide'}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden bg-white" role="tablist" data-testid="engagement-window">
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                onClick={() => setDays(w.value)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  days === w.value ? 'bg-teal-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`engagement-window-${w.value}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {/* Diagnostic banner — only renders when the user has sent emails
            in the last 30 days but ZERO engagement events have arrived.
            Most common cause: Resend webhook isn't subscribed to the
            `email.delivered` / `email.opened` etc. event types.
            Auto-hides as soon as a single delivery event lands. */}
        {diagnostics?.missing_delivery_subscriptions && (
          <div
            className="mb-6 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4"
            data-testid="engagement-diagnostics-banner"
          >
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 text-sm text-amber-900">
              <p className="font-semibold mb-1">No delivery events received in the last 24 hours.</p>
              <p className="text-amber-800 leading-relaxed">
                You&apos;ve sent emails, but Resend hasn&apos;t reported any opens, deliveries, or bounces back to CMS. Open
                {' '}<span className="font-medium">Resend &rarr; Webhooks &rarr; your endpoint &rarr; Events</span> and
                subscribe to <code className="px-1 py-0.5 bg-amber-100 rounded text-[11px]">email.delivered</code>,
                {' '}<code className="px-1 py-0.5 bg-amber-100 rounded text-[11px]">email.opened</code>,
                {' '}<code className="px-1 py-0.5 bg-amber-100 rounded text-[11px]">email.bounced</code>, and
                {' '}<code className="px-1 py-0.5 bg-amber-100 rounded text-[11px]">email.complained</code>.
              </p>
              {diagnostics.total_events_24h > 0 && (
                <p className="mt-2 text-[11px] text-amber-700">
                  {diagnostics.total_events_24h} other webhook event{diagnostics.total_events_24h === 1 ? '' : 's'} did arrive in the last 24h — so the endpoint is reachable, you just need to subscribe to the engagement event types.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Stat tiles */}
        {statsLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400 py-12 justify-center" data-testid="engagement-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading engagement…
          </div>
        ) : totalSent === 0 ? (
          <Card className="border-dashed border-gray-200 bg-white" data-testid="engagement-empty">
            <CardContent className="p-10 text-center">
              <Mail className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">
                No outbound emails in this {days}-day window {scope === 'mine' ? "for you yet" : "for your org yet"}.
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Send your first claim email from the Notes tab on any claim and the report will populate here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <StatTile Icon={Mail} label="Sent" value={stats.total_sent} color="text-gray-500" testid="tile-sent" />
            <StatTile Icon={CheckCheck} label="Delivered" value={stats.delivered}
              sub={`${stats.delivery_rate}% delivery rate`} color="text-sky-600" testid="tile-delivered" />
            <StatTile Icon={Eye} label="Opened" value={stats.opened}
              sub={`${stats.open_rate}% open rate · ${stats.total_opens} total opens`} color="text-emerald-600"
              testid="tile-opened" />
            <StatTile Icon={AlertTriangle} label="Bounced" value={stats.bounced}
              sub={`${stats.bounce_rate}% bounce rate`}
              color={stats.bounced > 0 ? 'text-red-600' : 'text-gray-400'} testid="tile-bounced" />
          </div>
        )}

        {/* Recent emails feed */}
        {!statsLoading && totalSent > 0 && (
          <Card className="border-none shadow-sm bg-white" data-testid="engagement-recent">
            <CardContent className="p-5 md:p-6">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4">
                Recent Outbound Emails
              </h2>
              {emailsLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : filteredEmails.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No recent outbound emails.</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredEmails.map((m) => (
                    <div key={m.email_id} className="py-3 flex items-start justify-between gap-3" data-testid={`engagement-row-${m.email_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 truncate">{m.subject || '(no subject)'}</span>
                          <DeliveryPill msg={m} />
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          To {(m.to || []).join(', ') || '—'} · Claim {m.report_no}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {m.sent_at ? new Date(m.sent_at).toLocaleString() : ''} · by {m.created_by_name || '—'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EmailEngagement;
